import React, { createContext, useEffect, useState, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { AuthContextType } from './auth-types';

interface RoleState {
  /** The user these roles were resolved for; null means "signed out". */
  userId: string | null;
  isAdmin: boolean;
  isStaff: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  signInWithGoogle: async () => {},
  signOut: async () => {},
  isLoading: true,
  isAdmin: false,
  isAdminLoading: true,
  isStaff: false,
  isStaffLoading: true,
});

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Roles are stamped with the user they were resolved for. Keeping them in one
  // piece of state (instead of separate isAdmin/isStaff/loading flags) is what
  // makes the "loading" flags below derived rather than set from an effect: the
  // instant `user` changes, the stamp stops matching and consumers see loading
  // again. With effect-set flags there was a render in between where the flags
  // still read false from the previous user, and PublicRoute would route a
  // freshly signed-in admin/staff user to /dashboard before their role landed.
  const [roles, setRoles] = useState<RoleState | null>(null);
  const hasTrackedLogin = useRef<string | null>(null);

  const currentUserId = user?.id ?? null;
  const rolesResolved = roles !== null && roles.userId === currentUserId;
  // The session itself is still unknown while isLoading, so roles can't be settled yet.
  const rolesLoading = isLoading || !rolesResolved;
  const isAdmin = rolesResolved && roles.isAdmin;
  const isStaff = rolesResolved && roles.isStaff;

  useEffect(() => {
    // Wait for the session to be known; resolving roles against a not-yet-loaded
    // session would stamp a result for the wrong (null) user.
    if (isLoading) return;

    if (!user) {
      setRoles({ userId: null, isAdmin: false, isStaff: false });
      return;
    }

    let cancelled = false;
    const userId = user.id;

    (async () => {
      const [adminResult, staffResult] = await Promise.all([
        supabase.rpc('is_admin'),
        supabase.rpc('is_staff'),
      ]);

      if (cancelled) return;

      if (adminResult.error) console.error('Error checking admin status:', adminResult.error);
      if (staffResult.error) console.error('Error checking staff status:', staffResult.error);

      setRoles({
        userId,
        isAdmin: adminResult.data ?? false,
        isStaff: staffResult.data ?? false,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [user, isLoading]);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      // Track login events only once per user session (non-blocking)
      if (event === 'SIGNED_IN' && session?.user && hasTrackedLogin.current !== session.user.id) {
        hasTrackedLogin.current = session.user.id;
        // Fire and forget - don't block auth state changes
        (async () => {
          try {
            await supabase.rpc('track_event', {
              p_event_type: 'auth_login',
              p_user_id: session.user.id,
              p_user_type: 'parent',
              p_metadata: { 
                provider: session.user.app_metadata.provider || 'email',
                browser: navigator.userAgent,
                url: window.location.href
              },
              p_email: session.user.email,
              p_user_agent: navigator.userAgent,
              p_page_url: window.location.href,
              p_referrer: document.referrer || null,
            });
          } catch (error) {
            console.error('Error tracking login event:', error);
          }
        })();
      }
      
      // Reset tracking flag on sign out
      if (event === 'SIGNED_OUT') {
        hasTrackedLogin.current = null;
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const value = {
    user,
    session,
    isLoading,
    isAdmin,
    isAdminLoading: rolesLoading,
    isStaff,
    isStaffLoading: rolesLoading,
    signInWithGoogle: async () => {
      try {
        const isPreviewDeployment = window.location.hostname.includes('vercel.app') && 
          !window.location.hostname.startsWith('bhcs.vercel.app');
        
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: isPreviewDeployment
              ? `${window.location.origin}/auth/callback`
              : process.env.NODE_ENV === 'production'
                ? 'https://portal.bhcskids.com/auth/callback'
                : `${window.location.origin}/auth/callback`,
            skipBrowserRedirect: false,
            queryParams: {
              access_type: 'offline',
              prompt: 'consent'
            }
          }
        });
        
        if (error) {
          console.error('Error during Google sign in:', error);
          throw error;
        }
      } catch (error) {
        console.error('Error in signInWithGoogle:', error);
        throw error;
      }
    },
    signOut: async () => {
      try {
        if (user) {
          await supabase.rpc('track_event', {
            p_event_type: 'auth_logout',
            p_user_id: user.id,
            p_user_type: 'parent',
            p_email: user.email,
          });
        }
        const { error } = await supabase.auth.signOut();
        if (error) {
          console.error('Error signing out:', error);
          return;
        }
        navigate('/login');
      } catch (error) {
        console.error('Error signing out:', error);
      }
    }
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}